import React, { useEffect, useMemo, useState } from "react";
import { TESTIMONIALS } from "@/data/testimonials";

const CAT_INDUSTRY = {
  fasteners: ["Fasteners", "Machinery"],
  "cycle-parts": ["Machinery"],
  textiles: ["Textile", "Fabric"],
  electronics: ["Machinery", "Lighting"],
  yiwu: ["Lighting", "Fabric"],
  furniture: ["Furniture"],
  medical: ["Machinery"],
};

function pickForCategory(slug, count = 2) {
  const want = CAT_INDUSTRY[slug] || [];
  const match = TESTIMONIALS.filter((t) => want.includes(t.industry));
  const rest = TESTIMONIALS.filter((t) => !want.includes(t.industry));
  return [...match, ...rest].slice(0, count);
}

function initials(name) {
  return name.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

function Avatar({ name, dark }) {
  return (
    <span style={{
      width: 44, height: 44, flex: "none", borderRadius: "50%",
      background: dark ? "#C08A2E" : "#0B2649", color: dark ? "#2A1D06" : "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 14,
    }}>{initials(name)}</span>
  );
}

// Rotating strip for the homepage; stable initial item for prerendered HTML.
export default function TestimonialStrip() {
  const items = useMemo(() => [...TESTIMONIALS], []);
  const [i, setI] = useState(0);
  useEffect(() => {
    if (window.__PRERENDER__ || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => setI((x) => (x + 1) % items.length), 5200);
    return () => clearInterval(id);
  }, [items.length]);
  const t = items[i];
  if (!t) return null;

  return (
    <section style={{ background: "#0B2649", color: "#fff", padding: "64px 0" }} data-testid="home-testimonials">
      <div className="wrap" style={{ maxWidth: 880, textAlign: "center" }}>
        <div className="k2" style={{ color: "#C08A2E" }}>WHAT CLIENTS SAY</div>
        <h2 style={{ color: "#fff", marginBottom: 30 }}>Trusted by importers across India &amp; Europe</h2>
        <div key={i} style={{ animation: "fadeIn .5s ease", minHeight: 150 }}>
          <p style={{ fontFamily: "'Fraunces', serif", fontSize: 22, lineHeight: 1.5, color: "#EAF1FB", margin: "0 auto 22px", maxWidth: 720 }}>
            <span style={{ color: "#C08A2E", fontSize: 34, verticalAlign: "-8px", marginRight: 4 }}>“</span>
            {t.quote}
            <span style={{ color: "#C08A2E", fontSize: 34, verticalAlign: "-8px", marginLeft: 2 }}>”</span>
          </p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
            <Avatar name={t.name} dark />
            <div style={{ textAlign: "left" }}>
              <div style={{ fontWeight: 700, fontSize: 15 }} data-testid="testimonial-name">{t.name}</div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, color: "#9FB8DA" }}>
                {t.city} · {t.industry}
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 26 }}>
          {items.map((_, n) => (
            <button key={n} aria-label={`Testimonial ${n + 1}`} onClick={() => setI(n)}
              data-testid={`testimonial-dot-${n}`}
              style={{
                width: n === i ? 26 : 9, height: 9, borderRadius: 999, border: "none", cursor: "pointer",
                background: n === i ? "#C08A2E" : "rgba(255,255,255,.3)", transition: "width .3s, background .3s",
              }} />
          ))}
        </div>
      </div>
    </section>
  );
}

// Static cards for category pages (relevant industries first, then random fill).
export function CategoryTestimonials({ slug, count = 2 }) {
  const items = useMemo(() => pickForCategory(slug, count), [slug, count]);
  if (!items.length) return null;
  return (
    <section id="client-stories" style={{ background: "var(--wash)" }} data-testid="category-testimonials">
      <div className="wrap" style={{ maxWidth: 860 }}>
        <div className="k2">CLIENT VOICES</div>
        <h2>What buyers say</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 16, marginTop: 26 }}>
          {items.map((t, i) => (
            <div key={i} data-testid={`category-testimonial-${i}`}
              style={{ background: "#fff", border: "1.5px solid var(--line)", borderRadius: 14, padding: 22, boxShadow: "0 8px 24px rgba(29,60,115,.06)" }}>
              <p style={{ fontFamily: "'Fraunces', serif", fontSize: 16.5, lineHeight: 1.5, color: "var(--navy)", marginBottom: 16 }}>
                <span style={{ color: "#C08A2E", fontSize: 26, verticalAlign: "-6px", marginRight: 2 }}>“</span>
                {t.quote}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Avatar name={t.name} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "var(--navy)" }}>{t.name}</div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11.5, color: "var(--grey)" }}>
                    {t.city} · {t.industry}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
